import { Capacitor } from '@capacitor/core';
import { supabase } from '../lib/supabase';

/**
 * mobilePushService — تسجيل Push عبر Firebase Cloud Messaging (Capacitor)
 *
 * iOS: كان @capacitor/push-notifications يعيد رمز APNs فقط، بينما الخادم يرسل عبر FCM —
 * لذلك نستخدم @capacitor-firebase/messaging للحصول على FCM token على الجهاز.
 */

let _registrationListenerAdded = false;
let _appListenerAdded = false;

/**
 * يطلب صلاحية الإشعارات ويحفظ FCM token في device_tokens.
 */
export async function registerMobilePush(userId) {
  if (!Capacitor.isNativePlatform()) {
    console.log('[mobilePush] skipped — not native platform');
    return;
  }
  if (!userId) {
    console.warn('[mobilePush] skipped — no userId');
    return;
  }

  try {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');

    // 1. فحص حالة الأذونات الحالية
    const permStatus = await FirebaseMessaging.checkPermissions();
    console.log('[mobilePush] permission status:', permStatus.receive);

    if (permStatus.receive === 'prompt') {
      console.log('[mobilePush] requesting permission...');
      const result = await FirebaseMessaging.requestPermissions();
      console.log('[mobilePush] permission result:', result.receive);
      if (result.receive !== 'granted') {
        console.warn('[mobilePush] ❌ permission denied by user');
        return;
      }
    } else if (permStatus.receive === 'denied') {
      console.warn('[mobilePush] ❌ permission previously denied — go to iOS Settings to re-enable');
      return;
    } else if (permStatus.receive !== 'granted') {
      console.warn('[mobilePush] ❌ unexpected permission state:', permStatus.receive);
      return;
    }

    console.log('[mobilePush] ✅ permission granted');

    if (!_registrationListenerAdded) {
      _registrationListenerAdded = true;

      await FirebaseMessaging.addListener('tokenReceived', async (event) => {
        const t = event?.token;
        console.log('[mobilePush] tokenReceived event, token length:', t?.length);
        if (t) await _saveToken(userId, t, Capacitor.getPlatform());
      });

      await FirebaseMessaging.addListener('notificationReceived', (notification) => {
        console.log('[mobilePush] foreground notification:', JSON.stringify(notification));
        window.dispatchEvent(new CustomEvent('mobilePushReceived', { detail: notification }));
      });

      await FirebaseMessaging.addListener('notificationActionPerformed', (action) => {
        const data = action.notification?.data;
        // تذكير التقييم — احفظ booking_id لـ MyDeliveries يفتح المودال تلقائياً
        if (data?.type === 'REVIEW_REMINDER' && data?.booking_id) {
          localStorage.setItem('review_reminder_booking_id', data.booking_id);
        }
        if (data?.booking_id) {
          const path = _getRouteFromData(data);
          if (path) window.location.hash = path;
        }
      });
    }

    // 2. تصفير الـ badge عند كل فتح للتطبيق
    if (!_appListenerAdded) {
      _appListenerAdded = true;
      const clearBadge = () => FirebaseMessaging.setBadge({ count: 0 }).catch(() => {});
      // يُنفَّذ عند عودة التطبيق للواجهة (native resume أو tab focus)
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') clearBadge();
      });
      // تصفير فوري الآن
      await clearBadge();
    }

    // 3. جلب FCM token مباشرة
    console.log('[mobilePush] calling getToken()...');
    const { token } = await FirebaseMessaging.getToken();
    if (token) {
      console.log('[mobilePush] ✅ FCM token obtained, length:', token.length);
      await _saveToken(userId, token, Capacitor.getPlatform());
    } else {
      console.warn('[mobilePush] ❌ getToken() returned empty token');
    }
  } catch (err) {
    console.warn('[mobilePush] registration failed:', err?.message || err);
  }
}

/**
 * إزالة المستمعات ومحاولة حذف رمز FCM محلياً.
 */
export async function unregisterMobilePush() {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { FirebaseMessaging } = await import('@capacitor-firebase/messaging');
    await FirebaseMessaging.removeAllListeners();
    await FirebaseMessaging.deleteToken().catch(() => {});
    _registrationListenerAdded = false;
  } catch (_) {
    /* non-critical */
  }
}

async function _saveToken(userId, token, platform) {
  if (!userId || !token) return;
  try {
    const { error } = await supabase.from('device_tokens').upsert(
      { user_id: userId, token, platform, updated_at: new Date().toISOString() },
      { onConflict: 'user_id,token' },
    );
    if (error) console.warn('[mobilePush] save token failed:', error.message);
    else console.log('[mobilePush] FCM token saved:', platform);
  } catch (err) {
    console.warn('[mobilePush] save token exception:', err);
  }
}

function _getRouteFromData(data) {
  const type = data?.type;
  const bookingId = data?.booking_id;

  if (!type) return null;

  switch (type) {
    case 'NEW_BOOKING':
      return '/photographer-dashboard/bookings?tab=pending';
    case 'AWAITING_PAYMENT':
      return bookingId ? `/booking-payment/${bookingId}` : '/customer-dashboard/bookings';
    case 'PAYMENT_CONFIRMED':
      return '/photographer-dashboard/bookings?tab=confirmed';
    case 'FILES_DELIVERED':
      return '/customer-dashboard/my-photos';
    case 'BOOKING_EXPIRED':
      return bookingId ? `/booking-expired/${bookingId}` : '/customer-dashboard/bookings';
    case 'CUSTOMER_CANCELLED':
      return '/photographer-dashboard/bookings';
    case 'SESSION_REMINDER':
      return '/customer-dashboard/bookings?tab=confirmed';
    case 'REVIEW_REMINDER':
      return '/customer-dashboard/my-photos';
    default:
      return '/customer-dashboard/bookings';
  }
}
